import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { cryptoService, PortfolioData, CryptoData } from '../../services/cryptoService';
import { authService } from '../../services/authService';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import CryptoListItem from '../../components/CryptoListItem/CryptoListItem';
import AllocationChart from '../../components/Charts/AllocationChart';
import PageTitle from '../../components/PageTitle/PageTitle';

const Portfolio: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portfolioData, setPortfolioData] = useState<PortfolioData>({
    cryptos: []
  });

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await cryptoService.getPortfolioData();
      setPortfolioData(result);
    } catch (err: any) {
      console.error('Erreur portfolio:', err);
      setError(err.message || 'Erreur lors du chargement du portefeuille');
      
      if (err.message?.includes('Session expirée') || err.message?.includes('Token JWT manquant')) {
        authService.logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCrypto = () => {
    navigate('/add-crypto');
  };

  const handleDeleteCrypto = async (symbol: string) => {
    try {
      const tokenData = localStorage.getItem('token');
      if (!tokenData) {
        throw new Error('Non authentifié');
      }

      let token;
      try {
        const parsedToken = JSON.parse(tokenData);
        token = parsedToken.token;
      } catch (e) {
        token = tokenData;
      }

      if (!token) {
        throw new Error('Token invalide');
      }

      // 1. Récupérer le portefeuille actuel
      const getResponse = await fetch(`https://api.cryptonvx.com/v1/app?jwt=${token}&type=data`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!getResponse.ok) {
        if (getResponse.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        throw new Error('Erreur lors de la récupération du portefeuille');
      }

      const portfolioData = await getResponse.json();
      
      // Initialiser les listes
      let pairs: string[] = [];
      let quantities: number[] = [];
      let bidPrices: number[] = [];

      // Si le portefeuille existe, récupérer les données
      if (Array.isArray(portfolioData) && portfolioData.length > 0) {
        const data = portfolioData[0];
        if (data.pairs && data.quantity && data.bid_price) {
          pairs = data.pairs.map((e: any) => e.toString());
          quantities = data.quantity.map((e: any) => parseFloat(e.toString()) || 0);
          bidPrices = data.bid_price.map((e: any) => parseFloat(e.toString()) || 0);
        }
      }

      // 2. Supprimer la crypto
      const index = pairs.indexOf(symbol);
      if (index !== -1) {
        pairs.splice(index, 1);
        quantities.splice(index, 1);
        bidPrices.splice(index, 1);
      }

      // 3. Envoyer le portefeuille mis à jour
      const updateResponse = await fetch(`https://api.cryptonvx.com/v1/app?jwt=${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'no-cors',
        body: JSON.stringify({
          pairs,
          quantity: quantities,
          bid_price: bidPrices,
        }),
      });

      // En mode no-cors, on ne peut pas lire la réponse
      // On considère que la requête a réussi si on arrive ici
      // Recharger les données du portefeuille
      loadPortfolioData();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'fr' ? 'fr-FR' : undefined, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout title={t('portfolio.title')}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <CircularProgress sx={{ color: androidColors.primaryGold }} />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title={t('portfolio.title')}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
          }}
        >
          <Alert
            severity="error"
            sx={{
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              color: androidColors.error,
              '& .MuiAlert-icon': {
                color: androidColors.error,
              },
            }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={loadPortfolioData}
                startIcon={<Refresh />}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title={t('portfolio.title')}>
      <PageTitle title={t('portfolio.pageTitle')} />
      {/* Graphique de répartition */}
      {portfolioData.cryptos.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <AllocationChart cryptos={portfolioData.cryptos} />
        </Box>
      )}

      {/* Header avec bouton d'ajout */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
          }}
        >
          {t('portfolio.myCryptos')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddCrypto}
          sx={{
            backgroundColor: androidColors.primaryGold,
            color: androidColors.darkBackground,
            '&:hover': {
              backgroundColor: androidColors.darkGold,
            },
          }}
        >
          {t('common.add')}
        </Button>
      </Box>

      {/* Liste des cryptomonnaies */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {portfolioData.cryptos.length > 0 ? (
          portfolioData.cryptos.map((crypto: CryptoData, index: number) => (
            <CryptoListItem
              key={`${crypto.symbol}-${index}`}
              crypto={crypto}
              index={index}
              onEdit={() => navigate('/edit-crypto', { state: { crypto, index } })}
              onDelete={async () => {
                try {
                  await handleDeleteCrypto(crypto.symbol);
                } catch (err) {
                  console.error('Erreur lors de la suppression:', err);
                }
              }}
              isDashboard={false}
            />
          ))
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: androidColors.textSecondary,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('portfolio.noCryptos')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t('portfolio.addFirstCrypto')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCrypto}
              sx={{
                backgroundColor: androidColors.primaryGold,
                color: androidColors.darkBackground,
                '&:hover': {
                  backgroundColor: androidColors.darkGold,
                },
              }}
            >
              {t('portfolio.addCryptoCta')}
            </Button>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default Portfolio;